import {
  centsToPlain,
  formatCents,
  type AccountId,
  type CategoryId,
  type Income,
  type IsoDate,
  type Snapshot,
  type Transaction,
  type TransactionId,
} from "@budgeter/shared";

/** The All accounts register's route parameter. */
export const allAccounts = "all";
export type RegisterScope = AccountId | typeof allAccounts;

export const incomeLabels: Record<Income, string> = {
  nextMonth: "Income for next month",
  thisMonth: "Income for this month",
};

export function transferLabel(accountName: string): string {
  return `Transfer: ${accountName}`;
}

/** Search text with any colon written as ": ", so "Transfer : ANZ" finds "Transfer: ANZ Visa". */
export function normaliseColons(text: string): string {
  return text.replace(/\s*:\s*/g, ": ");
}

/**
 * Whether typed text reads as a transfer to one of these accounts ("Transfer", "transfer :",
 * "Transfer: Home" for Home Loan), so it must not be offered as a new payee name.
 */
export function looksLikeTransfer(typed: string, accountNames: string[]): boolean {
  const match = /^transfer\b\s*:?\s*(.*)$/i.exec(typed.trim());
  if (!match) {
    return false;
  }
  const rest = (match[1] ?? "").toLowerCase();
  return rest === "" || accountNames.some((n) => n.toLowerCase().startsWith(rest));
}

export interface SplitRow {
  label: string;
  memo: string;
  amount: number;
}

export interface RegisterRow {
  id: TransactionId;
  txn: Transaction;
  accountName: string;
  payee: string;
  category: string;
  needsCategory: boolean;
  isTransfer: boolean;
  /** A transfer created by a split line; it's edited from its split. */
  isSplitCounterpart: boolean;
  splits: SplitRow[];
  /** Running balance after this row (in date order); null when not sorted by date. */
  balance: number | null;
}

export interface RegisterFilters {
  search: string;
  uncleared: boolean;
  unapproved: boolean;
  flagged: boolean;
  from: IsoDate | "";
  to: IsoDate | "";
  /** Rows in this category, directly or through a split line. */
  categoryId: CategoryId | "";
}

export const noFilters: RegisterFilters = {
  search: "",
  uncleared: false,
  unapproved: false,
  flagged: false,
  from: "",
  to: "",
  categoryId: "",
};

export type SortKey = "date" | "payee" | "category" | "amount";
export interface RegisterSort {
  key: SortKey;
  dir: "asc" | "desc";
}

export const defaultSort: RegisterSort = { key: "date", dir: "desc" };

function name(map: ReadonlyMap<string, { name: string }>, id: string | undefined): string {
  return (id && map.get(id)?.name) || "";
}

export function payeeLabel(s: Snapshot, t: Transaction): string {
  if (t.transfer) {
    return transferLabel(name(s.accounts, t.transfer.accountId));
  }
  return name(s.payees, t.payeeId);
}

export function categoryLabel(s: Snapshot, t: Transaction): string {
  const n = t.amountAndSplits.splits.length;
  if (n > 0) {
    return `Split (${n} ${n === 1 ? "line" : "lines"})`;
  }
  if (t.income) {
    return incomeLabels[t.income];
  }
  return name(s.categories, t.categoryId);
}

/** On-budget money needs a category: not a split, not income, not an on-budget transfer. */
export function needsCategory(s: Snapshot, t: Transaction): boolean {
  if (t.categoryId || t.income || t.amountAndSplits.splits.length > 0) {
    return false;
  }
  const own = s.accounts.get(t.accountId);
  if (!own?.onBudget) {
    return false;
  }
  if (t.transfer) {
    const other = s.accounts.get(t.transfer.accountId);
    return !other?.onBudget && !isSplitCounterpart(s, t);
  }
  return true;
}

export function isSplitCounterpart(s: Snapshot, t: Transaction): boolean {
  if (!t.transfer) {
    return false;
  }
  const parent = s.transactions.get(t.transfer.txnId);
  return !!parent?.amountAndSplits.splits.some((l) => l.transferTxnId === t.id);
}

function splitRows(s: Snapshot, t: Transaction): SplitRow[] {
  return t.amountAndSplits.splits.map((l) => ({
    label: l.transferAccountId
      ? transferLabel(name(s.accounts, l.transferAccountId))
      : l.income
        ? incomeLabels[l.income]
        : name(s.categories, l.categoryId) || "Needs a category",
    memo: l.memo,
    amount: l.amount,
  }));
}

/** Date order with a stable tiebreak, oldest first. */
function byDateAsc(a: Transaction, b: Transaction): number {
  return a.date < b.date ? -1 : a.date > b.date ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function inScope(t: Transaction, scope: RegisterScope): boolean {
  return !t.deleted && (scope === allAccounts || t.accountId === scope);
}

/**
 * The register's rows: every live transaction in scope with display labels, the running
 * balance (computed over all rows before filtering, so it matches the account), then
 * filtered and sorted.
 */
export function buildRegisterRows(
  s: Snapshot,
  scope: RegisterScope,
  filters: RegisterFilters = noFilters,
  sort: RegisterSort = defaultSort,
): RegisterRow[] {
  const txns: Transaction[] = [];
  for (const t of s.transactions.values()) {
    if (inScope(t, scope)) {
      txns.push(t);
    }
  }
  txns.sort(byDateAsc);
  let running = 0;
  const all: RegisterRow[] = txns.map((t) => {
    running += t.amountAndSplits.amount;
    return {
      id: t.id,
      txn: t,
      accountName: name(s.accounts, t.accountId),
      payee: payeeLabel(s, t),
      category: categoryLabel(s, t),
      needsCategory: needsCategory(s, t),
      isTransfer: !!t.transfer,
      isSplitCounterpart: isSplitCounterpart(s, t),
      splits: splitRows(s, t),
      balance: running,
    };
  });
  const rows = all.filter((r) => matches(r, filters));
  if (sort.key === "date") {
    if (sort.dir === "desc") {
      rows.reverse();
    }
    return rows;
  }
  const dir = sort.dir === "asc" ? 1 : -1;
  const key = (r: RegisterRow): string | number =>
    sort.key === "payee"
      ? r.payee.toLowerCase()
      : sort.key === "category"
        ? r.category.toLowerCase()
        : r.txn.amountAndSplits.amount;
  return rows
    .map((r) => ({ ...r, balance: null }))
    .sort((a, b) => {
      const ka = key(a);
      const kb = key(b);
      return ka < kb ? -dir : ka > kb ? dir : byDateAsc(b.txn, a.txn);
    });
}

function matches(r: RegisterRow, f: RegisterFilters): boolean {
  const t = r.txn;
  if (f.uncleared && t.cleared !== "uncleared") {
    return false;
  }
  if (f.unapproved && t.approved) {
    return false;
  }
  if (f.flagged && t.flag === "none") {
    return false;
  }
  if (f.from && t.date < f.from) {
    return false;
  }
  if (f.to && t.date > f.to) {
    return false;
  }
  if (
    f.categoryId &&
    t.categoryId !== f.categoryId &&
    !t.amountAndSplits.splits.some((l) => l.categoryId === f.categoryId)
  ) {
    return false;
  }
  const q = f.search.trim().toLowerCase();
  if (q === "") {
    return true;
  }
  const amount = Math.abs(t.amountAndSplits.amount);
  const haystack = [
    r.payee,
    r.category,
    t.memo,
    r.accountName,
    ...r.splits.flatMap((l) => [l.label, l.memo]),
    formatCents(amount),
    centsToPlain(amount),
    t.importNote?.text ?? "",
  ]
    .join("\n")
    .toLowerCase();
  return q.split(/\s+/).every((word) => haystack.includes(word.replace(/^[-−]/, "")));
}

export function filtersActive(f: RegisterFilters): boolean {
  return (
    f.search.trim() !== "" ||
    f.uncleared ||
    f.unapproved ||
    f.flagged ||
    !!f.from ||
    !!f.to ||
    !!f.categoryId
  );
}
